// ─────────────────────────────────────────────
// KLOTHIQ — Purchases List Page (wired)
// ─────────────────────────────────────────────

import { Input, Select } from '@/components/ui/FormControls';
import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import { usePurchases, usePurchasePeople, usePurchaseWorkspace } from '@/hooks/usePurchases';
import { useSuppliers } from '@/hooks/useSuppliers';
import DatePicker from '@/components/common/DatePicker';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import ExportActions from '@/components/common/ExportActions';
import { Plus, ShoppingBag, Search, ChevronRight, IndianRupee } from 'lucide-react';

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'partially_received', label: 'Partial' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'returned', label: 'Returned' },
];

function formatRupee(val: number) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

export default function PurchasesListPage() {
  const workspace = usePurchaseWorkspace();
  const people = usePurchasePeople(!!workspace.data);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [payment, setPayment] = useState('all');
  const [mode, setMode] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const suppliers = useSuppliers();
  const {
    data: purchases,
    isLoading,
    error,
    refetch,
  } = usePurchases({ status, search, supplier, payment, mode, from, to });
  const filtered = !!(
    search ||
    status !== 'all' ||
    supplier ||
    payment !== 'all' ||
    mode !== 'all' ||
    from ||
    to
  );
  const pageCount = Math.max(1, Math.ceil((purchases?.length || 0) / 25));
  const currentPage = Math.min(page, pageCount);
  const visiblePurchases = purchases?.slice((currentPage - 1) * 25, currentPage * 25) || [];

  const filterSummaryText =
    [
      status !== 'all' ? `Status: ${status}` : null,
      search ? `Search: "${search}"` : null,
      supplier
        ? `Supplier: ${suppliers.data?.find((s) => s.id === supplier)?.name || supplier}`
        : null,
      payment !== 'all' ? `Payment: ${payment}` : null,
      mode !== 'all' ? `Entry: ${mode}` : null,
      from ? `From: ${from}` : null,
      to ? `To: ${to}` : null,
    ]
      .filter(Boolean)
      .join(' | ') || 'All Purchases';

  return (
    <div>
      <PageHeader
        title="Purchases"
        description="Track purchase orders and goods receipt"
        actions={
          <div className="flex items-center gap-2">
            <ExportActions
              excelOptions={() => ({
                fileName: `Purchases_Report_${new Date().toISOString().split('T')[0]}`,
                sheetName: 'Purchases',
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'PO #', key: 'purchase_number' },
                  { header: 'Supplier', key: 'supplier' },
                  { header: 'Invoice #', key: 'invoice_no' },
                  { header: 'Date', key: 'purchase_date', format: 'date' },
                  {
                    header: 'Total Amount (₹)',
                    key: 'total_amount',
                    format: 'currency',
                    width: 18,
                  },
                  { header: 'Balance Due (₹)', key: 'balance_due', format: 'currency', width: 18 },
                  { header: 'Status', key: 'status' },
                  { header: 'Payment Status', key: 'payment_status' },
                  { header: 'Entry type', key: 'entry_mode' },
                  { header: 'Created by', key: 'created_by' },
                  { header: 'Paid (₹)', key: 'paid_amount', format: 'currency' },
                ],
                data: (purchases ?? []).map((p) => ({
                  purchase_number: p.purchase_number,
                  supplier: p.suppliers?.name ?? '—',
                  invoice_no: p.supplier_invoice_no ?? '—',
                  purchase_date: p.purchase_date,
                  total_amount: Number(p.total_amount),
                  balance_due: Number(p.balance_due),
                  status: p.status,
                  payment_status: p.payment_status,
                  entry_mode: p.entry_mode || 'catalog',
                  paid_amount: Number(p.paid_amount),
                  created_by:
                    people.data?.find((person) => person.id === p.created_by)?.full_name ||
                    'Recorded user',
                })),
              })}
              pdfOptions={() => ({
                fileName: `Purchases_Report_${new Date().toISOString().split('T')[0]}`,
                title: 'Purchase Orders Report',
                subtitle: `Exported on ${new Date().toLocaleDateString('en-IN')}`,
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'PO #', key: 'purchase_number' },
                  { header: 'Supplier', key: 'supplier' },
                  { header: 'Date', key: 'purchase_date', format: 'date' },
                  { header: 'Entry', key: 'entry_mode' },
                  { header: 'Paid (₹)', key: 'paid_amount', format: 'currency' },
                  { header: 'Total (₹)', key: 'total_amount', format: 'currency', width: 25 },
                  { header: 'Balance (₹)', key: 'balance_due', format: 'currency', width: 25 },
                  { header: 'Status', key: 'status' },
                  { header: 'Payment', key: 'payment_status' },
                ],
                rows: (purchases ?? []).map((p) => ({
                  purchase_number: p.purchase_number,
                  supplier: p.suppliers?.name ?? '—',
                  purchase_date: p.purchase_date,
                  entry_mode: p.entry_mode === 'direct' ? 'Direct' : 'Catalog',
                  paid_amount: Number(p.paid_amount),
                  total_amount: Number(p.total_amount),
                  balance_due: Number(p.balance_due),
                  status: p.status,
                  payment_status: p.payment_status,
                })),
                summaryLines: [
                  { label: 'Total Purchase Orders', value: (purchases ?? []).length.toString() },
                  {
                    label: 'Total Purchase Value',
                    value: `₹${(purchases ?? []).reduce((sum, p) => sum + Number(p.total_amount), 0).toFixed(2)}`,
                  },
                  {
                    label: 'Total Outstanding',
                    value: `₹${(purchases ?? []).reduce((sum, p) => sum + Number(p.balance_due), 0).toFixed(2)}`,
                  },
                ],
              })}
            />
            <Link
              to="/purchases/new"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> New Purchase
            </Link>
          </div>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Status tabs */}
        <div className="flex overflow-x-auto rounded-md border border-border bg-muted/30 p-0.5">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => {
                setStatus(s.value);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                status === s.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="purchase-search"
            type="text"
            aria-label="Search purchases"
            placeholder="PO, invoice or supplier…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="flex h-9 w-48 rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <label className="space-y-1 text-xs">
          <span>Supplier</span>
          <Select
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
              setPage(1);
            }}
            className="min-h-11 w-full rounded-xl border border-input bg-background px-2 text-sm"
            helpLabel="Supplier"
          >
            <option value="">All suppliers</option>
            {suppliers.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1 text-xs">
          <span>Payment</span>
          <Select
            value={payment}
            onChange={(e) => {
              setPayment(e.target.value);
              setPage(1);
            }}
            className="min-h-11 w-full rounded-xl border border-input bg-background px-2 text-sm"
            helpLabel="Payment"
          >
            <option value="all">All payments</option>
            <option value="unpaid">Unpaid</option>
            <option value="partially_paid">Partially paid</option>
            <option value="paid">Paid</option>
          </Select>
        </label>
        <label className="space-y-1 text-xs">
          <span>Entry type</span>
          <Select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPage(1);
            }}
            className="min-h-11 w-full rounded-xl border border-input bg-background px-2 text-sm"
            helpLabel="Entry type"
          >
            <option value="all">All entries</option>
            <option value="catalog">Catalog</option>
            <option value="direct">Direct entry</option>
          </Select>
        </label>
        <div className="space-y-1 text-xs">
          <label htmlFor="purchase-from">From</label>
          <DatePicker
            id="purchase-from"
            value={from}
            onChange={(v) => {
              setFrom(v || '');
              setPage(1);
            }}
            max={to || undefined}
            helpLabel="From"
          />
        </div>
        <div className="space-y-1 text-xs">
          <label htmlFor="purchase-to">To</label>
          <DatePicker
            id="purchase-to"
            value={to}
            onChange={(v) => {
              setTo(v || '');
              setPage(1);
            }}
            min={from || undefined}
            helpLabel="To"
          />
        </div>
      </div>

      {isLoading && <LoadingState message="Loading purchases…" />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && purchases?.length === 0 && (
        <EmptyState
          icon={<ShoppingBag className="h-12 w-12" />}
          title="No purchases found"
          description={filtered ? 'Try clearing filters' : 'Create your first purchase order'}
          action={
            !filtered ? (
              <Link
                to="/purchases/new"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                <Plus className="h-4 w-4" /> New Purchase
              </Link>
            ) : undefined
          }
        />
      )}

      {!isLoading && !error && purchases && purchases.length > 0 && (
        <>
          {/* Desktop */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">PO #</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Supplier
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Entry</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Amount</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Paid</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Balance Due
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visiblePurchases.map((p) => (
                  <tr key={p.id} className="cursor-pointer transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Link
                        to={`/purchases/${p.id}`}
                        className="font-mono text-xs font-semibold uppercase text-primary hover:underline"
                      >
                        {p.purchase_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground">
                      {p.suppliers?.name ?? '—'}
                      <p className="mt-1 text-xs font-normal text-muted-foreground">
                        Created by{' '}
                        {people.data?.find((person) => person.id === p.created_by)?.full_name ||
                          'Recorded user'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.purchase_date}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {p.entry_mode === 'direct' ? 'Direct entry' : 'Catalog'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">
                      {formatRupee(Number(p.total_amount))}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {formatRupee(Number(p.paid_amount))}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${Number(p.balance_due) > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                    >
                      {Number(p.balance_due) > 0 ? formatRupee(Number(p.balance_due)) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} size="sm" />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.payment_status} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm md:hidden">
            {visiblePurchases.map((p) => (
              <Link
                key={p.id}
                to={`/purchases/${p.id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30"
              >
                <div>
                  <p className="font-medium text-foreground">{p.suppliers?.name ?? 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground">
                    Created by{' '}
                    {people.data?.find((person) => person.id === p.created_by)?.full_name ||
                      'Recorded user'}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    <span className="font-mono uppercase text-primary">{p.purchase_number}</span>
                    {` · ${p.purchase_date} · ${p.entry_mode === 'direct' ? 'Direct entry' : 'Catalog'}`}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusBadge status={p.status} size="sm" />
                    <StatusBadge status={p.payment_status} size="sm" />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Paid {formatRupee(Number(p.paid_amount))} · Balance{' '}
                    {formatRupee(Number(p.balance_due))}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="flex items-center gap-0.5 text-sm font-semibold text-foreground">
                    <IndianRupee className="h-3 w-3" />
                    {Number(p.total_amount).toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {purchases.length} purchase{purchases.length !== 1 ? 's' : ''}
          </p>
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <button
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              className="min-h-11 rounded-xl border border-border px-4 disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              {currentPage} / {pageCount}
            </span>
            <button
              disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)}
              className="min-h-11 rounded-xl border border-border px-4 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
